import { useMemo } from 'react';
import { Box } from '@mui/material';
import { CheckCircleRounded } from '@mui/icons-material';
import { motion, useReducedMotion } from 'framer-motion';
import { CWY_COLORS } from '../../theme/colors';

/** Animated green payment confirmation tick (shared by result page + chat details). */
export function PaymentSuccessCheck({ size = 'lg' }: { size?: 'sm' | 'lg' }) {
  const reduceMotion = useReducedMotion();
  const green = CWY_COLORS.verified;
  const badge = size === 'sm' ? 72 : 112;
  const wrap = size === 'sm' ? 96 : 148;
  const checkSvg = size === 'sm' ? 36 : 56;
  const stroke = size === 'sm' ? 4.5 : 5.5;

  const sparks = useMemo(
    () =>
      Array.from({ length: size === 'sm' ? 8 : 10 }, (_, i) => {
        const n = size === 'sm' ? 8 : 10;
        const angle = (i / n) * Math.PI * 2;
        const radius = size === 'sm' ? 52 : 78;
        return {
          id: i,
          x: Math.cos(angle) * radius,
          y: Math.sin(angle) * radius,
          delay: 0.18 + i * 0.02,
        };
      }),
    [size]
  );

  if (reduceMotion) {
    return (
      <Box
        sx={{
          width: badge,
          height: badge,
          mx: 'auto',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(145deg, ${green}, #0f8f52)`,
          boxShadow: `0 12px 28px ${green}55`,
        }}
      >
        <CheckCircleRounded sx={{ fontSize: badge * 0.5, color: '#fff' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width: wrap,
        height: wrap,
        mx: 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {[0, 1].map((i) => (
        <motion.div
          key={`ring-${i}`}
          aria-hidden
          style={{
            position: 'absolute',
            width: badge,
            height: badge,
            borderRadius: '50%',
            border: `2px solid ${green}`,
          }}
          initial={{ scale: 0.55, opacity: 0.7 }}
          animate={{ scale: 2.05, opacity: 0 }}
          transition={{
            duration: 0.95,
            delay: 0.12 + i * 0.14,
            ease: [0.16, 1, 0.3, 1],
          }}
        />
      ))}

      <motion.div
        aria-hidden
        style={{
          position: 'absolute',
          width: badge + 18,
          height: badge + 18,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${green}66 0%, transparent 70%)`,
        }}
        initial={{ scale: 0.4, opacity: 0 }}
        animate={{ scale: [0.4, 1.35, 1.1], opacity: [0, 0.85, 0.35] }}
        transition={{ duration: 0.9, ease: 'easeOut' }}
      />

      {sparks.map((s) => (
        <motion.span
          key={s.id}
          aria-hidden
          style={{
            position: 'absolute',
            width: size === 'sm' ? 5 : 7,
            height: size === 'sm' ? 5 : 7,
            borderRadius: '50%',
            background: s.id % 2 === 0 ? green : '#34d399',
            boxShadow: `0 0 10px ${green}`,
          }}
          initial={{ x: 0, y: 0, opacity: 0, scale: 0.4 }}
          animate={{ x: s.x, y: s.y, opacity: [0, 1, 0], scale: [0.4, 1, 0.2] }}
          transition={{ duration: 0.7, delay: s.delay, ease: 'easeOut' }}
        />
      ))}

      <motion.div
        style={{
          position: 'relative',
          zIndex: 2,
          width: badge,
          height: badge,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(145deg, #22c55e 0%, ${green} 48%, #0b7a45 100%)`,
          boxShadow: `
            0 22px 44px rgba(30, 157, 99, 0.45),
            0 0 0 6px rgba(30, 157, 99, 0.12),
            inset 0 1px 0 rgba(255,255,255,0.45)
          `,
        }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: [0, 1.22, 0.94, 1.04, 1], opacity: 1 }}
        transition={{ duration: 0.7, times: [0, 0.45, 0.7, 0.88, 1], ease: 'easeOut' }}
      >
        <svg width={checkSvg} height={checkSvg} viewBox="0 0 56 56" fill="none" aria-hidden>
          <motion.path
            d="M14 29.5 L24 39.5 L42 17.5"
            stroke="#fff"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 0.42, delay: 0.28, ease: [0.16, 1, 0.3, 1] }}
          />
        </svg>
      </motion.div>
    </Box>
  );
}
